import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getBrandingConfig } from '../api/brandingSettings';
import { useTheme } from './ThemeContext';
import { platformColoursFor, usePlatformBranding } from './PlatformBrandingContext';

/**
 * BrandingContext
 * Manages app-wide branding including logo, company name, and dynamic colors.
 * Applies CSS custom properties for colors when custom app colors are enabled.
 * Caches branding in localStorage for instant load on subsequent visits.
 *
 * Until someone signs in, the branding is the platform's only (PlatformBrandingContext: its name,
 * logo and contact details, and the colours the admin hub set for this BMS or the platform's): no
 * business's branding is ever fetched without a session. Signed in, each field the business has
 * set wins; the platform's fills the rest. `config` is the branding in effect; `storedConfig`
 * is what the business saved.
 */

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

const BRANDING_CACHE_KEY = 'bms-branding-cache';
// Sent by AuthContext when the signed-in person or business changes (a literal here: AuthContext
// sits inside this provider)
const SESSION_CHANGED = 'bms-session-changed';

/** The business the cached branding is for: the signed-in one, or this app's own accounts. */
const cacheKey = () => {
  try {
    const tenant = JSON.parse(localStorage.getItem('tenant') || 'null');
    return `${BRANDING_CACHE_KEY}:${tenant?.id || 'local'}`;
  } catch {
    return `${BRANDING_CACHE_KEY}:local`;
  }
};

import { getCurrencySymbol } from '../utils/currency';
import { brandPalette, rgbChannels, scaleOf, sidebarColours, withPlatformColours } from '../utils/brandColors';

// Default branding values
const defaultBranding = {
  // A business's own details come from its branding; nothing is shown until they load
  company_name: '',
  company_email: '',
  company_phone: '',
  company_address: '',
  currency: 'NGN',
  smart_decimals: true,
  timezone: 'Africa/Lagos',
  date_format: 'DD/MM/YYYY',
  logo_url: null,
  logo_type: 'icon',
  logo_icon_text: '',
  use_logo_as_watermark: false,
  watermark_opacity: 0.1,
  // Brand colours: the platform's (withPlatformColours) until the business chooses its own
  primary_color: null,
  accent_color: null,
  text_color: '#333333',
  background_color: '#FFFFFF',
  sidebar_name_size: 'medium',
  // App UI colors (status colours mean something - paid, warning, error - so they stay)
  app_primary_color: null,
  app_accent_color: null,
  app_accent_light: null,
  app_accent_dark: null,
  app_success_color: '#10B981',
  app_warning_color: '#F59E0B',
  app_danger_color: '#EF4444',
  enable_custom_app_colors: false,
  // One Manager's colours until the business chooses its own
  use_platform_colors: true,
  // Wholesale module colors: the platform's too until the business chooses its own
  wholesale_primary_color: null,
  wholesale_secondary_color: null,
  wholesale_accent_color: null,
  wholesale_currency: 'NGN',
  wholesale_smart_decimals: true
};

/** Whether someone is signed in (only then is there a business's branding to show). */
const hasSession = () => {
  try {
    return Boolean(localStorage.getItem('accessToken'));
  } catch {
    return false;
  }
};

// Read the signed-in business's cached branding (instant, no network); none without a session
const getCachedBranding = () => {
  if (!hasSession()) return null;
  try {
    const cached = localStorage.getItem(cacheKey());
    if (cached) return JSON.parse(cached);
  } catch {
    /* corrupt cache — ignore */
  }
  return null;
};

/** "One Manager" → "OM": the logo's icon text when there is no logo image. */
const initialsOf = (name) =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');

/** The platform's identity in branding terms: what shows wherever a business hasn't set its own. */
const platformIdentity = (platform) => ({
  company_name: platform?.name || '',
  company_email: platform?.contact?.email || '',
  company_phone: platform?.contact?.phone || '',
  company_address: platform?.contact?.address || '',
  ...(platform?.logo?.imageUrl
    ? { logo_url: platform.logo.imageUrl, logo_type: 'image', logo_icon_text: '' }
    : { logo_url: null, logo_type: 'icon', logo_icon_text: initialsOf(platform?.logo?.text || platform?.name) })
});

/** The fields a business has actually set (empty ones fall back to the platform's). */
const ownFields = (business) => {
  if (!business) return {};
  const own = Object.fromEntries(
    Object.entries(business).filter(([, value]) => value !== '' && value !== null && value !== undefined)
  );
  // No logo of its own (neither image nor icon text): the platform's logo shows
  if (!own.logo_url && !own.logo_icon_text) delete own.logo_type;
  return own;
};

const saveBrandingCache = (config) => {
  try {
    localStorage.setItem(cacheKey(), JSON.stringify(config));
  } catch {
    /* storage full — ignore */
  }
};

// Apply CSS custom properties to document root
const applyBrandColors = (config, theme, currentTheme) => {
  const root = document.documentElement;
  const set = (name, value) => root.style.setProperty(name, value);
  // Every brand colour is the brand colour in effect or a shade of it (utils/brandColors.js)
  const palette = brandPalette(config);

  set('--color-accent', palette.accent);
  set('--color-accent-light', palette.accentLight);
  set('--color-accent-dark', palette.accentDark);
  // The same as channels, for Tailwind classes with transparency (bg-accent-gold/10)
  set('--color-accent-rgb', rgbChannels(palette.accent));
  set('--color-accent-light-rgb', rgbChannels(palette.accentLight));
  set('--color-accent-dark-rgb', rgbChannels(palette.accentDark));
  set('--accent-gold', palette.accent);
  set('--accent-gold-light', palette.accentLight);
  set('--accent-gold-dark', palette.accentDark);
  // Status colours mean something (paid, warning, error): the business may set them
  set('--color-success', config.app_success_color || '#10B981');
  set('--color-warning', config.app_warning_color || '#F59E0B');
  set('--color-danger', config.app_danger_color || '#EF4444');

  // Retail colors (set in /settings/branding)
  set('--retail-primary', palette.primary);
  set('--retail-accent', palette.accent);
  set('--retail-border', palette.border);
  set('--retail-hover', palette.hover);
  set('--retail-text', palette.text);

  // Wholesale colors: its own (set in /wholesale/settings/branding), or the retail ones when
  // the wholesale module is set to use the retail branding colours
  set('--wholesale-primary', palette.wholesalePrimary);
  set('--wholesale-secondary', palette.wholesaleSecondary);
  set('--wholesale-accent', palette.wholesaleAccent);
  set('--wholesale-text', palette.wholesaleText);
  set('--wholesale-light', palette.wholesaleLight);
  // The wholesale module's look (Tailwind emerald-*): the wholesale brand colour's scale
  for (const [step, colour] of Object.entries(scaleOf(palette.wholesaleAccent))) {
    set(`--wholesale-${step}-rgb`, rgbChannels(colour));
  }
  // Chart series colours (--brand-series-1 … 6)
  palette.series.forEach((colour, i) => set(`--brand-series-${i + 1}`, colour));

  // Sidebar surfaces: each user's theme, or the branding's sidebar colour blended with it
  // (recomputed when the theme changes). The accent is the brand's either way.
  const retailSidebar = sidebarColours(config, 'retail', theme);
  const wholesaleSidebar = sidebarColours(config, 'wholesale', theme);
  for (const [module, colours] of [
    ['retail', retailSidebar],
    ['wholesale', wholesaleSidebar]
  ]) {
    for (const [key, value] of Object.entries(colours)) {
      root.style.setProperty(`--${module}-sidebar-${key}`, value);
    }
  }
};

export const BrandingProvider = ({ children }) => {
  const { theme, currentTheme } = useTheme();
  const platform = usePlatformBranding();
  // The signed-in business's branding as saved (cached for instant render), or null
  const [business, setBusiness] = useState(getCachedBranding);
  const platformColours = platformColoursFor(platform);
  const storedConfig = useMemo(
    () => ({ ...defaultBranding, ...platformIdentity(platform), ...ownFields(business) }),
    [business, platform]
  );
  const config = useMemo(
    () => withPlatformColours(storedConfig, platformColours),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the colours, not the object identity
    [storedConfig, platformColours.primaryColor, platformColours.accentColor]
  );
  const [loading, setLoading] = useState(hasSession);
  const [error, setError] = useState(null);

  const fetchBranding = useCallback(async () => {
    // Nobody signed in: the platform's branding only, never a business's
    if (!hasSession()) {
      setBusiness(null);
      setLoading(false);
      return;
    }
    try {
      // 1. Get tenant ID from storage or URL
      const storedTenant = localStorage.getItem('tenant');
      const tenant = storedTenant ? JSON.parse(storedTenant) : null;
      let tenantId = tenant?.id;

      if (!tenantId) {
        const searchParams = new URLSearchParams(window.location.search);
        tenantId = searchParams.get('tenantId');
      }

      const response = await getBrandingConfig(tenantId);
      const data = response?.data || response || null;
      setBusiness(data);
      if (data) saveBrandingCache(data); // persist for instant load next time
      setError(null);
    } catch (err) {
      console.warn('Could not fetch branding config, using defaults:', err?.message || err);
      // On error, keep whatever is in state (cached or default)
      setError(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // The business's branding on mount when signed in, and again whenever someone signs in or out
  // (a sign-in from the platform starts on a page that knows no business yet). Until it arrives,
  // that business's cached copy; signed out, the platform's.
  useEffect(() => {
    fetchBranding();
    const reload = () => {
      setBusiness(getCachedBranding());
      fetchBranding();
    };
    window.addEventListener(SESSION_CHANGED, reload);
    return () => window.removeEventListener(SESSION_CHANGED, reload);
  }, [fetchBranding]);

  // Apply colors whenever config or theme changes
  useEffect(() => {
    applyBrandColors(config, theme, currentTheme);
  }, [config, theme, currentTheme]);

  // The tab title and favicon are the platform's, never the business's (hub contract §3.1):
  // PlatformBrandingContext sets them.

  // Function to refresh branding (call after saving settings)
  const refreshBranding = useCallback(async () => {
    await fetchBranding();
  }, [fetchBranding]);

  // Function to update config locally (for live preview)
  const updateConfigLocally = useCallback((updates) => {
    setBusiness((prev) => {
      const updated = { ...(prev || {}), ...updates };
      saveBrandingCache(updated);
      return updated;
    });
  }, []);

  // Block rendering until branding is loaded from the database.
  // The localStorage cache makes subsequent loads near-instant.
  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#F0F2F5'
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: '3px solid #E5E7EB',
            borderTopColor: brandPalette(config).accent,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <BrandingContext.Provider
      value={{
        config,
        storedConfig,
        platformColours,
        currency: config.currency || 'NGN',
        currencySymbol: getCurrencySymbol(config.currency || 'NGN'),
        loading,
        error,
        refreshBranding,
        updateConfigLocally
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
